import { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { CalendarDays, Check } from "lucide-react";
import { formatTourDates } from "@/lib/fields";

const toDate = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const fmt = (d) =>
  `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;

// value: array of MM/DD/YYYY strings. onChange: (array) => void
// Rendered inline (no portal) so it works reliably inside modal dialogs.
export const MultiDatePicker = ({ value, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  const dates = (Array.isArray(value) ? value : value ? [value] : [])
    .map(toDate)
    .filter(Boolean);

  const handleSelect = (selected) => {
    const arr = (selected || []).sort((a, b) => a - b).map(fmt);
    onChange(arr);
  };

  const summary = dates.length
    ? `${formatTourDates(value) || `${dates.length} date${dates.length > 1 ? "s" : ""}`} · ${dates.length} day${dates.length > 1 ? "s" : ""}`
    : "";

  return (
    <div>
      <Button
        type="button"
        variant="outline"
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        className="h-10 w-full justify-start border-zinc-800 bg-zinc-900 font-normal hover:bg-zinc-800"
      >
        <CalendarDays className="mr-2 h-4 w-4 shrink-0 text-[#E0575B]" />
        {dates.length ? (
          <span className="truncate text-zinc-200" data-testid={`${testid}-summary`}>{summary}</span>
        ) : (
          <span className="text-zinc-400">Pick tour date(s)</span>
        )}
      </Button>

      {open && (
        <div className="mt-2 rounded-md border border-zinc-800 bg-zinc-950 p-3" data-testid={`${testid}-calendar`}>
          <Calendar
            mode="multiple"
            selected={dates}
            onSelect={handleSelect}
            className="w-full p-0"
            classNames={{
              months: "w-full",
              month: "w-full space-y-3",
              caption: "flex justify-center pt-1 relative items-center",
              caption_label: "text-sm font-600 text-zinc-100",
              table: "w-full border-collapse",
              head_row: "flex w-full",
              head_cell: "flex-1 text-center text-[0.75rem] font-normal text-zinc-400",
              row: "flex w-full mt-1.5",
              cell: "flex-1 relative p-0.5 text-center text-sm focus-within:relative focus-within:z-20",
              day: "mx-auto flex h-11 w-full items-center justify-center rounded-md p-0 font-normal text-zinc-100 hover:bg-zinc-800 aria-selected:opacity-100",
              day_selected: "bg-[#9B2226] text-white hover:bg-[#7A1A1E] hover:text-white focus:bg-[#9B2226]",
              day_today: "ring-1 ring-inset ring-[#E0575B]/70 text-[#E0575B]",
              day_outside: "text-zinc-600",
              day_disabled: "text-zinc-700 opacity-40",
            }}
          />
          <Button
            type="button"
            size="sm"
            onClick={() => setOpen(false)}
            className="mt-1 w-full bg-[#9B2226] text-white hover:bg-[#7A1A1E]"
            data-testid={`${testid}-done`}
          >
            <Check className="mr-1.5 h-4 w-4" /> Done
          </Button>
        </div>
      )}
    </div>
  );
};
